import { useEffect, useMemo, useState } from 'react';
import {
  collection,
  doc,
  onSnapshot,
  query,
  where
} from 'firebase/firestore';
import {
  CheckCircle2,
  CircleDollarSign,
  Clock3,
  FileCheck2,
  ReceiptText,
  WalletCards,
  X
} from 'lucide-react';
import { db } from '../../core/firebase/firebase';

const text = (value) => String(value ?? '').trim();
const money = (value) => `${Number(value || 0).toFixed(0)} DT`;

const dateValue = (value) => {
  if (!value) return 0;
  if (typeof value?.toMillis === 'function') return value.toMillis();
  if (value?.seconds) return value.seconds * 1000;
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatDate = (value) => {
  const timestamp = dateValue(value);
  return timestamp
    ? new Date(timestamp).toLocaleDateString('fr-FR')
    : '—';
};

const paymentMethodLabel = (value) =>
  ({
    cash: 'Espèces',
    transfer: 'Virement bancaire',
    cheque: 'Chèque',
    card: 'TPE / carte',
    online: 'Paiement en ligne',
    multiple: 'Plusieurs modes',
    other: 'Autre'
  })[value] || value || '—';


const paymentDisplayLabel = (payment) => {
  if (text(payment?.paymentLabel)) return text(payment.paymentLabel);
  if (payment?.creditOnly || payment?.paymentCategory === 'family_credit') {
    return 'Avance de paiement / Crédit famille';
  }
  const allocation = Array.isArray(payment?.allocations)
    ? payment.allocations[0]
    : null;
  const label = text(allocation?.label);
  if (/inscription/i.test(label)) return 'Paiement inscription';
  return label ? `Paiement ${label}` : 'Paiement';
};

const paymentStatusLabel = (payment) => {
  if (payment?.settlementStatus === 'partial') return 'Paiement partiel';
  if (payment?.settlementStatus === 'paid') return 'Soldé';
  if (payment?.settlementStatus === 'credit' || payment?.paymentCategory === 'family_credit') {
    return 'Avance / crédit';
  }
  return 'Payé';
};

const contractLabel = (contract) => {
  if (
    contract?.status === 'active' ||
    contract?.active === true
  ) {
    return 'Actif';
  }

  if (contract?.status === 'pending_manager_approval') {
    return 'En validation';
  }

  if (contract?.status === 'discount_rejected') {
    return 'Remise refusée';
  }

  if (contract?.status === 'draft') {
    return 'Brouillon';
  }

  return 'En préparation';
};

export default function ParentFinanceSection({
  parentId,
  parent,
  children,
  user
}) {
  const [liveParent, setLiveParent] = useState(parent || null);
  const [contracts, setContracts] = useState([]);
  const [payments, setPayments] = useState([]);
  const [contractsOpen, setContractsOpen] = useState(false);
  const [selectedPaymentChildId, setSelectedPaymentChildId] = useState('');
  const [paymentReference, setPaymentReference] = useState('all');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!parentId) return undefined;

    const offParent = onSnapshot(
      doc(db, 'parents', parentId),
      (snapshot) => {
        if (snapshot.exists()) {
          setLiveParent({ id: snapshot.id, ...snapshot.data() });
        }
      }
    );

    const offContracts = onSnapshot(
      query(
        collection(db, 'contracts'),
        where('parentId', '==', parentId)
      ),
      (snapshot) => {
        setContracts(
          snapshot.docs
            .map((item) => ({ id: item.id, ...item.data() }))
            .sort(
              (a, b) =>
                dateValue(b.createdAt) - dateValue(a.createdAt)
            )
        );
      },
      (err) => {
        console.error(err);
        setError('Impossible de charger les contrats.');
      }
    );

    const offPayments = onSnapshot(
      query(
        collection(db, 'payments'),
        where('parentId', '==', parentId)
      ),
      (snapshot) => {
        setPayments(
          snapshot.docs
            .map((item) => ({ id: item.id, ...item.data() }))
            .sort(
              (a, b) =>
                dateValue(b.createdAt) - dateValue(a.createdAt)
            )
        );
      },
      (err) => {
        console.error(err);
        setError('Impossible de charger les paiements.');
      }
    );

    return () => {
      offParent();
      offContracts();
      offPayments();
    };
  }, [parentId]);

  useEffect(() => {
    const childIds = (children || []).map((child) => child.id).filter(Boolean);
    if (!childIds.length) {
      setSelectedPaymentChildId('');
      return;
    }

    if (!childIds.includes(selectedPaymentChildId)) {
      setSelectedPaymentChildId(childIds[0]);
      setPaymentReference('all');
    }
  }, [children, selectedPaymentChildId]);

  const latestContracts = useMemo(() => {
    const map = new Map();

    contracts.forEach((contract) => {
      if (contract.studentId && !map.has(contract.studentId)) {
        map.set(contract.studentId, contract);
      }
    });

    return children.map((child) => ({
      child,
      contract: map.get(child.id) || null
    }));
  }, [contracts, children]);

  const totalContracts = contracts.reduce(
    (sum, contract) => sum + Number(contract.totalAmount || 0),
    0
  );

  const totalPaid = contracts.reduce(
    (sum, contract) => sum + Number(contract.paidAmount || 0),
    0
  );

  const totalRemaining = contracts.reduce(
    (sum, contract) => sum + Number(contract.remainingAmount || 0),
    0
  );

  const familyAdvancePayments = payments.filter(
    (payment) =>
      !payment.studentId ||
      payment.creditOnly === true ||
      payment.paymentCategory === 'family_credit'
  );

  const childPayments = selectedPaymentChildId === '__family__'
    ? familyAdvancePayments
    : payments.filter(
        (payment) =>
          payment.studentId === selectedPaymentChildId &&
          payment.creditOnly !== true &&
          payment.paymentCategory !== 'family_credit'
      );

  const paymentReferences = useMemo(() => {
    const labels = new Set(childPayments.map(paymentDisplayLabel));
    return [...labels].sort((a, b) => a.localeCompare(b, 'fr'));
  }, [childPayments]);

  const visiblePayments = childPayments.filter(
    (payment) =>
      paymentReference === 'all' ||
      paymentDisplayLabel(payment) === paymentReference
  );

  const selectedPaymentChild = (children || []).find(
    (child) => child.id === selectedPaymentChildId
  );


  return (
    <section className="parent-finance-section">
      {error && (
        <div className="alert alert-error parent-alert">
          {error}
        </div>
      )}

      {message && (
        <div className="success-note">
          <CheckCircle2 size={18} />
          {message}
        </div>
      )}

      <div className="parent-finance-kpis parent-finance-kpis-simple">
        <article>
          <FileCheck2 />
          <div>
            <span>Total famille</span>
            <strong>{money(totalContracts)}</strong>
          </div>
        </article>

        <article>
          <CircleDollarSign />
          <div>
            <span>Total payé</span>
            <strong>{money(totalPaid)}</strong>
          </div>
        </article>

        <article>
          <Clock3 />
          <div>
            <span>Reste famille</span>
            <strong>{money(totalRemaining)}</strong>
          </div>
        </article>

        <article>
          <WalletCards />
          <div>
            <span>Crédit famille</span>
            <strong>{money(liveParent?.familyCredit || 0)}</strong>
          </div>
        </article>
      </div>

      <section className="parent-card-v0 parent-finance-card parent-contract-access-card">
        <div>
          <span className="parent-card-icon">
            <FileCheck2 size={21} />
          </span>

          <div>
            <p className="eyebrow">CONTRATS</p>
            <h3>Contrats des enfants</h3>
            <p>
              Consultez les montants, l’échéancier et acceptez les
              contrats envoyés par l’école.
            </p>
          </div>
        </div>

        <button
          type="button"
          className="button button-primary"
          onClick={() => setContractsOpen(true)}
        >
          Voir les contrats
        </button>
      </section>

      <section className="parent-card-v0 parent-finance-card">
        <div className="parent-card-v0-heading parent-payment-heading">
          <div>
            <p className="eyebrow">PAIEMENTS</p>
            <h3>Historique par enfant</h3>
            <p>
              Consultez les reçus d’un enfant à la fois, puis filtrez-les par référence de paiement.
            </p>
          </div>
        </div>

        <div className="parent-payment-child-tabs" role="tablist" aria-label="Choisir un enfant">
          {(children || []).map((child) => (
            <button
              type="button"
              key={child.id}
              className={selectedPaymentChildId === child.id ? 'active' : ''}
              onClick={() => {
                setSelectedPaymentChildId(child.id);
                setPaymentReference('all');
              }}
            >
              {text(child.fullName || child.fullname) || 'Élève'}
            </button>
          ))}

          {familyAdvancePayments.length > 0 && (
            <button
              type="button"
              className={selectedPaymentChildId === '__family__' ? 'active' : ''}
              onClick={() => {
                setSelectedPaymentChildId('__family__');
                setPaymentReference('all');
              }}
            >
              Avances famille
            </button>
          )}
        </div>

        <div className="parent-payment-filter-row">
          <div>
            <span>Historique affiché</span>
            <strong>
              {selectedPaymentChildId === '__family__'
                ? 'Avances et crédit famille'
                : text(selectedPaymentChild?.fullName || selectedPaymentChild?.fullname) || 'Enfant'}
            </strong>
          </div>

          <label>
            Référence
            <select
              value={paymentReference}
              onChange={(event) => setPaymentReference(event.target.value)}
            >
              <option value="all">Toutes les références</option>
              {paymentReferences.map((label) => (
                <option key={label} value={label}>{label}</option>
              ))}
            </select>
          </label>
        </div>

        <div className="parent-payment-history parent-payment-history-detailed">
          {visiblePayments.length ? (
            visiblePayments.map((payment) => {
              const remaining = Number(payment.referenceRemainingAmount || 0);
              const total = payment.referenceTotalAmount;
              const isPartial = payment.settlementStatus === 'partial' && remaining > 0;
              const partsCount = Array.isArray(payment.paymentParts)
                ? payment.paymentParts.length
                : 1;

              return (
                <article key={payment.id}>
                  <span className="parent-list-icon">
                    <ReceiptText size={18} />
                  </span>

                  <div className="parent-payment-main">
                    <strong>{paymentDisplayLabel(payment)}</strong>
                    <span>
                      Reçu {payment.receiptNumber || '—'} · {paymentMethodLabel(payment.paymentMethod)}
                    </span>
                    <span>
                      {payment.lastPaymentDate || payment.paymentDate || '—'}
                      {partsCount > 1 ? ` · ${partsCount} versements` : ''}
                    </span>
                  </div>

                  <div className="parent-payment-amounts">
                    <strong>{money(payment.amount)}</strong>
                    {total != null && (
                      <span>Montant prévu : {money(total)}</span>
                    )}
                    {isPartial ? (
                      <span className="parent-payment-remaining">
                        Reste à payer : {money(remaining)}
                      </span>
                    ) : (
                      <span className="parent-payment-paid">
                        {paymentStatusLabel(payment)}
                      </span>
                    )}
                  </div>
                </article>
              );
            })
          ) : (
            <div className="parent-empty">
              Aucun paiement enregistré pour cette sélection.
            </div>
          )}
        </div>
      </section>

      {contractsOpen && (
        <div
          className="modal-layer"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              setContractsOpen(false);
            }
          }}
        >
          <section className="parent-contract-modal panel">
            <header>
              <div>
                <p className="eyebrow">CONTRATS</p>
                <h2>Contrats des enfants</h2>
              </div>

              <button
                type="button"
                className="drawer-close"
                onClick={() => setContractsOpen(false)}
                aria-label="Fermer"
              >
                <X size={20} />
              </button>
            </header>

            <div className="parent-contract-modal-body">
              {latestContracts.map(({ child, contract }) => (
                <article
                  className="parent-contract-item"
                  key={child.id}
                >
                  <div className="parent-contract-item-heading">
                    <div>
                      <strong>
                        {text(child.fullName || child.fullname) ||
                          'Élève'}
                      </strong>
                      <span>
                        {child.level ||
                          child.requestedLevel ||
                          child.classLabel ||
                          'Niveau à confirmer'}
                      </span>
                    </div>

                    <span
                      className={`parent-contract-state ${
                        contract?.status === 'active' ||
contract?.active === true
  ? 'accepted'
                          : contract
                            ? 'waiting'
                            : 'missing'
                      }`}
                    >
                      {contract
                        ? contractLabel(contract)
                        : 'Non disponible'}
                    </span>
                  </div>

                  {contract ? (
                    <>
                      <div className="contract-summary-v0">
                        <div>
                          <span>Total</span>
                          <strong>
                            {money(contract.totalAmount)}
                          </strong>
                        </div>
                        <div>
                          <span>Payé</span>
                          <strong>
                            {money(contract.paidAmount)}
                          </strong>
                        </div>
                        <div>
                          <span>Reste</span>
                          <strong>
                            {money(contract.remainingAmount)}
                          </strong>
                        </div>
                      </div>

                      <div className="parent-contract-installments">
                        {(contract.installments || []).map(
                          (item) => (
                            <div key={item.id}>
                              <span>
                                <strong>{item.label}</strong>
                                <small>{item.dueDate || '—'}</small>
                              </span>
                              <span>
                                <strong>{money(item.amount)}</strong>
                                <small>
                                  Reste{' '}
                                  {money(
                                    item.remainingAmount ??
                                      Number(item.amount || 0) -
                                        Number(item.paidAmount || 0)
                                  )}
                                </small>
                              </span>
                            </div>
                          )
                        )}
                      </div>

                     {(contract.status === 'active' ||
  contract.active === true) && (
  <div className="success-note">
    <CheckCircle2 size={18} />
    Contrat actif
  </div>
)}
                    </>
                  ) : (
                    <div className="parent-empty">
                      Le contrat n’a pas encore été préparé par
                      l’administration.
                    </div>
                  )}
                </article>
              ))}
            </div>
          </section>
        </div>
      )}
    </section>
  );
}
